import { ExplodingHead } from './exploding-head/ExplodingHead';
import { Howl } from 'howler';
import { ScrollTriggerSection } from './scroll-trigger/ScrollTriggerSection';

// Import the sound file URLs
const thudSound = new URL('../sfx/thud.ogg', import.meta.url);
const doubleKillSound = new URL('../sfx/doublekill.ogg', import.meta.url);
const tripleKillSound = new URL('../sfx/triplekill.ogg', import.meta.url);
const ultraKillSound = new URL('../sfx/ultrakill.ogg', import.meta.url);
const rampageSound = new URL('../sfx/rampage.ogg', import.meta.url);
const unstoppableSound = new URL('../sfx/unstoppable.ogg', import.meta.url);

// Lock scrolling initially
document.body.classList.add('scroll-locked');

// Load sound effects
const explosionSound = new Howl({
    src: [thudSound.href],
    volume: 0.7,
    html5: true,
    onloaderror: (id, error) => {
        console.error('Sound failed to load:', error);
    },
    onplayerror: (id, error) => {
        console.error('Sound failed to play:', error);
    }
});

const doubleKillHowl = new Howl({
    src: [doubleKillSound.href],
    volume: 0.7,
    html5: true,
    onloaderror: (id, error) => {
        console.error('Double kill sound failed to load:', error);
    }
});

const tripleKillHowl = new Howl({
    src: [tripleKillSound.href],
    volume: 0.7,
    html5: true,
    onloaderror: (id, error) => {
        console.error('Triple kill sound failed to load:', error);
    }
});

const ultraKillHowl = new Howl({
    src: [ultraKillSound.href],
    volume: 0.7,
    html5: true,
    onloaderror: (id, error) => {
        console.error('Ultra kill sound failed to load:', error);
    }
});

const rampageHowl = new Howl({
    src: [rampageSound.href],
    volume: 0.7,
    html5: true,
    onloaderror: (id, error) => {
        console.error('Rampage sound failed to load:', error);
    }
});

const unstoppableHowl = new Howl({
    src: [unstoppableSound.href],
    volume: 0.7,
    html5: true,
    onloaderror: (id, error) => {
        console.error('Unstoppable sound failed to load:', error);
    }
});

// Unlock audio on first user interaction
let audioUnlocked = false;
const unlockAudio = () => {
    if (!audioUnlocked) {
        // Create and play a silent sound to unlock audio
        const silentHowl = new Howl({
            src: ['data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA='],
            volume: 0.01,
            autoplay: true
        });
        audioUnlocked = true;
        
        // Remove listeners
        window.removeEventListener('scroll', unlockAudio);
        window.removeEventListener('click', unlockAudio);
        window.removeEventListener('touchstart', unlockAudio);
    }
};

// Add listeners for various user interactions
window.addEventListener('scroll', unlockAudio, { once: true });
window.addEventListener('click', unlockAudio, { once: true });
window.addEventListener('touchstart', unlockAudio, { once: true });

// Unlock slider functionality
const unlockSlider = document.querySelector('.unlock-slider') as HTMLElement;
const sliderHandle = document.getElementById('unlockHandle') as HTMLElement;
const sliderTrack = document.querySelector('.slider-track') as HTMLElement;
const sliderText = document.querySelector('.slider-text') as HTMLElement;
let isUnlocked = false;

if (unlockSlider && sliderHandle) {
    let isDragging = false;
    let startX = 0;
    let handleStartX = 0;
    const sliderWidth = 200; // Max width of slider
    const handleWidth = 34;
    const maxHandleX = sliderWidth - handleWidth - 6; // Account for padding
    
    const startDrag = (clientX: number) => {
        if (isUnlocked) return;
        isDragging = true;
        startX = clientX;
        handleStartX = parseInt(sliderHandle.style.left || '3');
        sliderHandle.style.transition = 'none';
        sliderTrack.style.transition = 'none';
    };
    
    const drag = (clientX: number) => {
        if (!isDragging || isUnlocked) return;
        
        const deltaX = clientX - startX;
        let newX = handleStartX + deltaX;
        
        // Constrain to slider bounds
        newX = Math.max(3, Math.min(newX, maxHandleX));
        
        sliderHandle.style.left = newX + 'px';
        
        // Update track width
        const progress = (newX - 3) / (maxHandleX - 3);
        sliderTrack.style.width = (progress * 100) + '%';
        
        // Fade out "Locked" text as slider progresses
        if (sliderText) {
            sliderText.style.opacity = String(1 - progress);
        }
        
        // Check if unlocked
        if (newX >= maxHandleX - 10) {
            unlock();
        }
    };
    
    const endDrag = () => {
        if (!isDragging || isUnlocked) return;
        isDragging = false;
        
        // Snap back if not fully dragged
        const currentX = parseInt(sliderHandle.style.left || '3');
        if (currentX < maxHandleX - 10) {
            sliderHandle.style.transition = 'left 0.3s ease';
            sliderTrack.style.transition = 'width 0.3s ease';
            sliderHandle.style.left = '3px';
            sliderTrack.style.width = '0';
            
            // Restore "Locked" text
            if (sliderText) {
                sliderText.style.transition = 'opacity 0.3s ease';
                sliderText.style.opacity = '1';
            }
        }
    };
    
    const unlock = () => {
        isUnlocked = true;
        sliderHandle.classList.add('unlocked');
        sliderHandle.innerHTML = '✓';
        
        // Keep handle at the right position
        sliderHandle.style.left = '';
        sliderHandle.style.right = '3px';
        
        // Change text to "Unlocked"
        if (sliderText) {
            sliderText.textContent = 'Unlocked';
            sliderText.style.opacity = '1';
            sliderText.style.color = 'white';
        }
        
        // Keep track filled
        sliderTrack.style.width = '100%';
        
        // Unlock scrolling
        document.body.classList.remove('scroll-locked');
        
        // Show scroll indicator
        const scrollIndicator = document.querySelector('.scroll-indicator');
        if (scrollIndicator) {
            scrollIndicator.classList.add('visible');
        }
        
        // Also unlock audio since user has interacted
        unlockAudio();
    };
    
    // Mouse events
    sliderHandle.addEventListener('mousedown', (e) => startDrag(e.clientX));
    document.addEventListener('mousemove', (e) => drag(e.clientX));
    document.addEventListener('mouseup', endDrag);
    
    // Touch events
    sliderHandle.addEventListener('touchstart', (e) => startDrag(e.touches[0].clientX));
    document.addEventListener('touchmove', (e) => drag(e.touches[0].clientX));
    document.addEventListener('touchend', endDrag);
}

// Smooth scroll for anchor links
document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href'));
        if (target) {
            target.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    });
});

// Animate scroll indicator
const scrollIndicator = document.querySelector('.scroll-indicator');
if (scrollIndicator) {
    window.addEventListener('scroll', () => {
        const scrolled = window.scrollY;
        if (scrolled > 100) {
            scrollIndicator.style.opacity = '0';
        } else {
            scrollIndicator.style.opacity = '0.5';
        }
    });
}

// Basic presentation scroll handling (placeholder for future reveal.js-style implementation)
const presentationSlides = document.querySelectorAll('.presentation-container .slide');
if (presentationSlides.length > 0) {
    // Add intersection observer for slide animations
    const slideObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('slide-visible');
            }
        });
    }, { threshold: 0.3 });

    presentationSlides.forEach(slide => {
        slideObserver.observe(slide);
    });
}

// Horizontal scroll for project gallery with mouse wheel
const projectGallery = document.querySelector('.project-gallery');
if (projectGallery) {
    projectGallery.addEventListener('wheel', (e) => {
        if (e.deltaY !== 0) {
            e.preventDefault();
            projectGallery.scrollLeft += e.deltaY;
        }
    });
}

// Exploding head feature
const profileContainer = document.querySelector('.profile-image-container') as HTMLElement;
const profileHeadOverlay = document.querySelector('.profile-head-overlay') as HTMLImageElement;

if (profileContainer && profileHeadOverlay) {
    let hasTriggered = false;
    let lastScrollY = window.scrollY;
    let explodingHead: ExplodingHead | null = null;
    let explosionCount = 0; // Track number of explosions
    
    // Create canvas element
    const canvas = document.createElement('canvas') as HTMLCanvasElement;
    canvas.style.position = 'fixed';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.zIndex = '9999';
    canvas.style.pointerEvents = 'none';
    canvas.style.display = 'none';
    
    // Set canvas size
    const resizeCanvas = () => {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
    
    // Add canvas to body
    document.body.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    
    // Use scroll event for more precise detection
    window.addEventListener('scroll', () => {
        const currentScrollY = window.scrollY;
        const isScrollingDown = currentScrollY > lastScrollY;
        lastScrollY = currentScrollY;
        
        // Skip if already triggered or not scrolling down
        if (hasTriggered || !isScrollingDown) return;
        
        const rect = profileContainer.getBoundingClientRect();
        const containerTop = rect.top;
        
        // Trigger when the top of the head area reaches viewport top
        const triggerPoint = 0; // pixels from top of container
        
        if (containerTop <= -triggerPoint && containerTop >= -(triggerPoint + 100)) {
                hasTriggered = true;
                explosionCount++; // Increment explosion counter
                
                // Play explosion sound
                explosionSound.play();
                
                // Update h2 text based on explosion count
                const h2Element = document.querySelector('#hero h2');
                if (h2Element) {
                    switch (explosionCount) {
                        case 1:
                            h2Element.textContent = 'And Rampant Silliness';
                            break;
                        case 2:
                            h2Element.innerHTML = '-- D o u b l e &nbsp;&nbsp;&nbsp;&nbsp;&nbsp; K i l l --';
                            break;
                        case 3:
                            h2Element.textContent = 'You seem to like that';
                            break;
                        case 4:
                            h2Element.textContent = 'A little bit too much';
                            break;
                        case 5:
                            h2Element.textContent = 'Jesus, am I that bad?';
                            break;
                        case 6:
                            h2Element.textContent = 'I guess I must be 😭';
                            break;
                        case 7:
                            h2Element.textContent = 'Technology Leadership';
                            break;
                        // 8+ stays as Technology Leadership
                    }
                }
                
                // Play additional sounds based on explosion count
                setTimeout(() => {
                    switch (explosionCount) {
                        case 2:
                            doubleKillHowl.play();
                            break;
                        case 3:
                            tripleKillHowl.play();
                            break;
                        case 4:
                            ultraKillHowl.play();
                            break;
                        case 5:
                            rampageHowl.play();
                            break;
                        case 6:
                            unstoppableHowl.play();
                            break;
                        // 7+ only plays thud (no additional sound)
                    }
                }, 300);
                
                // Hide head
                profileHeadOverlay.style.visibility = 'hidden';
                profileHeadOverlay.style.opacity = '1'; // Ensure it's fully opaque when hidden
                
                // Show canvas
                canvas.style.display = 'block';
                
                // Create and start explosion
                const containerRect = profileContainer.getBoundingClientRect();
                explodingHead = new ExplodingHead(canvas, profileHeadOverlay);
                explodingHead.explode(containerRect, window.scrollY);
                
                // Start fade-in of head overlay after 3 seconds
                setTimeout(() => {
                    // First, make it visible but transparent
                    profileHeadOverlay.style.opacity = '0';
                    profileHeadOverlay.style.visibility = 'visible';
                    
                    // Force browser to process the above changes
                    void profileHeadOverlay.offsetWidth;
                    
                    // Now add transition and fade in
                    profileHeadOverlay.style.transition = 'opacity 1s ease-in-out';
                    profileHeadOverlay.style.opacity = '1';
                }, 3000);
                
                // Wait 4 seconds then clean up
                setTimeout(() => {
                    // Stop physics
                    if (explodingHead) {
                        explodingHead.stop();
                        explodingHead = null;
                    }
                    
                    // Hide canvas
                    canvas.style.display = 'none';
                    if (ctx) {
                        ctx.clearRect(0, 0, canvas.width, canvas.height);
                    }
                    
                    // Reset head overlay transition
                    profileHeadOverlay.style.transition = '';
                    profileHeadOverlay.style.opacity = '';
                    
                    // Reset trigger
                    hasTriggered = false;
                }, 4000);
        }
    });
    
}

// Initialize ScrollTrigger section
document.addEventListener('DOMContentLoaded', () => {
    new ScrollTriggerSection();
    // testScrollTrigger(); // Test basic ScrollTrigger functionality
});